import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2, MessageSquare, Info } from 'lucide-react';
import { useSenders } from '@/hooks/use-senders';
import { useSettingsStore } from '@/stores/settings-store';

/**
 * HighLevel-only "sending number" setting.
 *
 * HighLevel never tells a conversation provider which number to send FROM —
 * the outbound webhook carries only the recipient — so the sub-account's
 * sending number has to be chosen here and read back server-side.
 *
 * It is deliberately its own preference key rather than reusing the Inbox /
 * Compose / Chat defaults: those are per-view UI conveniences a user changes
 * freely, whereas this one decides what every message leaving the HighLevel
 * inbox is sent from, including messages no one is watching.
 *
 * SMS only: a WhatsApp sender is accepted by Twilio and then fails to deliver
 * an SMS, so those are filtered out rather than offered and silently dropped.
 */
export function HighSenderSettings({ bare = false }: { bare?: boolean } = {}) {
  const { allSenders, loading } = useSenders();
  // Org-level, not personal: a sub-account has one sending number, and every
  // Ulgebra user on that sub-account must see and share the same one.
  const highSender = useSettingsStore((s) => s.highSender);
  const updateHighSender = useSettingsStore((s) => s.updateHighSender);

  const selectedId = highSender?.id;
  const smsSenders = allSenders.filter(
    (s) => String(s.channel || '').toLowerCase() === 'sms' && !!s.phoneNumber,
  );

  /** Pass an empty id to clear the selection. */
  const select = (id: string) => {
    const sender = id ? smsSenders.find((s) => s.id === id) : undefined;
    updateHighSender(
      sender
        ? { id: sender.id, label: sender.label, channel: sender.channel, phoneNumber: sender.phoneNumber }
        : null,
    );
  };

  const body = (
    <div className="space-y-1.5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your Twilio numbers…
          </div>
        )}

        {!loading && smsSenders.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No SMS-capable numbers found. Connect Twilio, or add an SMS number to your Twilio account.
          </p>
        )}

        {!loading && smsSenders.map((s) => {
          const isSelected = selectedId === s.id;
          // The label frequently just restates the number in another format,
          // which stacks the same value twice under itself. Keep it only when
          // it carries a real name.
          const digits = (s.phoneNumber || '').replace(/\D/g, '');
          const subtitle = (s.label || '')
            .replace(/\([^)]*\d[^)]*\)/g, '')
            .replace(/\+?\d[\d\s()-]{5,}/g, '')
            .replace(/[()]/g, '')
            .trim()
            .replace(/^[\s·|,-]+|[\s·|,-]+$/g, '');
          const showSubtitle = !!subtitle && subtitle.replace(/\D/g, '') !== digits;
          return (
            <div
              key={s.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 leading-tight">
                  <p className="text-sm font-medium truncate">{s.phoneNumber}</p>
                  {showSubtitle && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>
                  )}
                </div>
              </div>
              {isSelected ? (
                <div className="flex items-center gap-2 shrink-0">
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                    <Check className="h-3.5 w-3.5" />
                    Sending number
                  </span>
                  {/* Clearing is only offered in settings — inside the first-run
                      dialog a cleared value would just re-open it immediately. */}
                  {!bare && (
                    <Button size="sm" variant="ghost" onClick={() => select('')}>
                      Clear
                    </Button>
                  )}
                </div>
              ) : (
                <Button size="sm" variant="outline" className="shrink-0" onClick={() => select(s.id)}>
                  Use this
                </Button>
              )}
            </div>
          );
        })}

        <div className="mt-4 flex gap-2.5 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3">
          <Info className="h-4 w-4 shrink-0 text-blue-600 mt-0.5" />
          <p className="text-xs leading-relaxed text-muted-foreground">
            HighLevel&rsquo;s own conversation inbox supports <strong>SMS only</strong>.
            To send WhatsApp messages, use the <strong>Inbox</strong> in this app &mdash;
            you&rsquo;ll find it in the left side menu.
          </p>
        </div>
    </div>
  );

  if (bare) return body;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">HighLevel Sending Number</CardTitle>
        <CardDescription>
          Messages sent from the HighLevel inbox go out from this number. Only SMS-capable
          numbers are listed — a WhatsApp number cannot deliver an SMS.
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
